import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";

interface BusinessUser {
  id: string;
  email: string;
  fullName?: string | null;
}

interface BusinessAuthContextType {
  user: BusinessUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  register: (email: string, password: string, fullName?: string) => Promise<{ user: BusinessUser | null; error: string | null }>;
  login: (email: string, password: string) => Promise<{ user: BusinessUser | null; error: string | null }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const BusinessAuthContext = createContext<BusinessAuthContextType | undefined>(undefined);

export function BusinessAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<BusinessUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();

      if (session?.user) {
        setUser({
          id: session.user.id,
          email: session.user.email || "",
          fullName: session.user.user_metadata?.full_name,
        });
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error("Error refreshing user:", error);
      setUser(null);
    }
  }, []);

  useEffect(() => {
    // Register the auth state listener FIRST so we never miss events,
    // then kick off the initial getSession(). The listener fires
    // synchronously for the current session state, which guarantees
    // isLoading is set to false only after we know the real auth state.
    // This fixes the PWA re-open bug where the old code's race between
    // refreshUser() and the listener caused a flash of logged-out state.
    let coldStartLogged = false;
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!coldStartLogged) {
          coldStartLogged = true;
          console.log(
            `[auth] cold-start session: ${session?.user ? "found" : "missing"}`
          );
        }
        // Supabase fires this on EVERY token refresh (~hourly) and on tab-focus
        // recovery. Keep the SAME `user` object reference when nothing actually
        // changed, so a no-op TOKEN_REFRESHED / SIGNED_IN doesn't churn every
        // useBusinessAuth() consumer (and every effect keyed on `user`) — the
        // root of the dashboard's periodic flicker/reset.
        setUser((prev) => {
          if (!session?.user) return prev === null ? prev : null;
          const next: BusinessUser = {
            id: session.user.id,
            email: session.user.email || "",
            fullName: session.user.user_metadata?.full_name,
          };
          if (prev && prev.id === next.id && prev.email === next.email && prev.fullName === next.fullName) {
            return prev;
          }
          return next;
        });
        setIsLoading(false);
      }
    );

    // getSession() reads from localStorage and emits through the
    // listener above — no need to duplicate the state-setting logic.
    supabase.auth.getSession();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const register = useCallback(async (email: string, password: string, fullName?: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
        },
      });

      if (error) {
        return { user: null, error: error.message };
      }

      if (data.user) {
        const newUser = {
          id: data.user.id,
          email: data.user.email || "",
          fullName: data.user.user_metadata?.full_name,
        };
        setUser(newUser);
        return { user: newUser, error: null };
      }
      return { user: null, error: "Registration failed" };
    } catch (error: any) {
      return { user: null, error: error.message || "Registration failed" };
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { user: null, error: error.message };
      }

      if (data.user) {
        const loggedUser = {
          id: data.user.id,
          email: data.user.email || "",
          fullName: data.user.user_metadata?.full_name,
        };
        setUser(loggedUser);
        return { user: loggedUser, error: null };
      }
      return { user: null, error: "Login failed" };
    } catch (error: any) {
      return { user: null, error: error.message || "Login failed" };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
  }, []);

  // Memoized so the context value's identity only changes when `user` or
  // `isLoading` actually change (register/login/logout/refreshUser are
  // useCallback-stable). Without this, every provider render handed consumers a
  // brand-new value object and re-rendered the whole app.
  const value = useMemo<BusinessAuthContextType>(
    () => ({ user, isAuthenticated: !!user, isLoading, register, login, logout, refreshUser }),
    [user, isLoading, register, login, logout, refreshUser],
  );

  return (
    <BusinessAuthContext.Provider value={value}>
      {children}
    </BusinessAuthContext.Provider>
  );
}

export function useBusinessAuth() {
  const context = useContext(BusinessAuthContext);
  if (context === undefined) {
    throw new Error("useBusinessAuth must be used within a BusinessAuthProvider");
  }
  return context;
}
